import type {
  ChannelsResponse,
  UsersSearchResponse,
  ChannelMessagesResponse,
  SendMessageRequest,
  SendMessageResponse,
  CreateChannelRequest,
  CreateChannelResponse,
  UpdateChannelNameRequest,
  UpdateChannelNameResponse,
  LeaveChannelResponse,
  Channel,
  SearchUserType,
  ChatContextType,
} from "@/features/chat/types";
import { fetcher } from "@/shared/lib/fetcher.client";

export const chatLegacyService = {
  // The legacy /channels endpoint has no server-side context filtering, so contextType
  // is intentionally ignored here — the client-side channelFilter is the only guard.
  async getChannels(_params?: { contextType?: ChatContextType }): Promise<ChannelsResponse> {
    const response = await fetcher.get<ChannelsResponse>("/channels");
    return response.data;
  },

  async getChannelById(channelId: number | string): Promise<Channel> {
    const response = await fetcher.get<{ channel: Channel }>(`/channels/${channelId}`);
    return response.data.channel;
  },

  async searchUsers(searchQuery: string, page: number = 1, types?: SearchUserType[]): Promise<UsersSearchResponse> {
    const params: Record<string, unknown> = {
      ...(searchQuery.length > 0 ? { "q[username_i_cont]": searchQuery } : {}),
      page,
    };

    if (types && types.length > 0) {
      params["q[type_eq]"] = types;
    }

    const response = await fetcher.get<UsersSearchResponse>("/users", {
      params,
      paramsSerializer: { indexes: null },
    });
    return response.data;
  },

  async getChannelMessages(channelId: number | string): Promise<ChannelMessagesResponse> {
    const response = await fetcher.get<ChannelMessagesResponse>(`/channels/${channelId}/messages`);
    return response.data;
  },

  async sendMessage(channelId: string, messageData: SendMessageRequest): Promise<SendMessageResponse> {
    const response = await fetcher.post<{ message: SendMessageResponse }>(`/channels/${channelId}/messages`, messageData);
    return response.data.message;
  },

  async createChannel(data: CreateChannelRequest): Promise<CreateChannelResponse> {
    const response = await fetcher.post<CreateChannelResponse>("/channels", data);
    return response.data;
  },

  async updateChannelName(channelId: number | string, data: UpdateChannelNameRequest): Promise<UpdateChannelNameResponse> {
    const response = await fetcher.patch<UpdateChannelNameResponse>(`/channels/${channelId}`, data);
    return response.data;
  },

  async leaveChannel(channelId: number | string): Promise<LeaveChannelResponse> {
    const response = await fetcher.patch<LeaveChannelResponse>(`/channels/${channelId}/leave`);
    return response.data;
  },
};
